import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAlertModal } from "../../components/admin/AlertModal";
import Spinner from "../../components/ui/Spinner";
import { orgAdminService } from "../../features/org-admin/service";
import type { OrganizationSummary } from "../../features/org-admin/types";
import { formatISTDateTime } from "../../utils/dateUtils";

const StatCard: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-2xl font-bold text-white mt-1">{value}</p>
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

const OrgAdminDashboardPage: React.FC = () => {
  const [org, setOrg] = useState<OrganizationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const { alertModal, showAlert } = useAlertModal();

  const fetchOrg = useCallback(async () => {
    setLoading(true);
    try {
      setOrg(await orgAdminService.getOrganization());
    } catch (err: any) {
      console.error(err);
      showAlert(err?.response?.data?.detail || "Failed to load your organization.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  if (loading) {
    return <div className="py-20 flex justify-center"><Spinner /></div>;
  }

  if (!org) {
    return (
      <>
        <div className="bg-gray-900 border border-gray-800 rounded-xl py-16 text-center text-gray-500 text-sm">
          Your organization could not be loaded. Try refreshing, or contact Gullivr support.
        </div>
        {alertModal}
      </>
    );
  }

  const seatsUsed = org.active_member_count;
  const seatLimit = org.seat_limit || 0;
  const seatsLeft = Math.max(seatLimit - seatsUsed, 0);
  const usagePercent = seatLimit > 0 ? Math.min(Math.round((seatsUsed / seatLimit) * 100), 100) : 0;

  return (
    <>
      <AdminPageHeader
        title={org.name}
        subtitle="Your organization at a glance — seat usage and who is still waiting to be invited."
        actions={<RefreshButton onRefresh={fetchOrg} />}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard
          label="Members"
          value={`${seatsUsed}/${seatLimit}`}
          hint={seatsLeft === 0 ? "All seats in use" : `${seatsLeft} seat${seatsLeft === 1 ? "" : "s"} free`}
        />
        <StatCard label="Awaiting acceptance" value={org.invited_count} hint="Invited, not joined yet" />
        <StatCard label="People you can invite" value={org.candidate_count} hint="Curated by Gullivr" />
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-gray-300">Seat usage</p>
          <p className="text-sm text-gray-500">{usagePercent}%</p>
        </div>
        <div className="h-2 w-full rounded-full bg-gray-800 overflow-hidden">
          <div
            className={`h-full rounded-full ${seatsLeft === 0 ? "bg-amber-500" : "bg-indigo-500"}`}
            style={{ width: `${usagePercent}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-3">
          {seatsUsed === 0 ? (
            <>
              0 members so far, {org.candidate_count} {org.candidate_count === 1 ? "person" : "people"} you can invite.{" "}
              <Link to="/org-admin/candidates" className="text-indigo-400 hover:text-indigo-300">Start inviting</Link>.
            </>
          ) : (
            <>
              Seat changes are handled by Gullivr — contact support to add seats.
            </>
          )}
        </p>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h2 className="text-sm font-medium text-gray-300 mb-3">Organization details</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-xs text-gray-500">Status</dt>
            <dd>
              <span className={`inline-block mt-1 px-2 py-1 rounded-md text-xs font-medium ${
                org.status === "active" ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"
              }`}>
                {org.status}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Identifier</dt>
            <dd className="text-gray-300 mt-1 break-all">{org.slug}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Created</dt>
            <dd className="text-gray-300 mt-1">{formatISTDateTime(org.created_at)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500">Last updated</dt>
            <dd className="text-gray-300 mt-1">{formatISTDateTime(org.updated_at)}</dd>
          </div>
        </dl>
      </div>
      {alertModal}
    </>
  );
};

export default OrgAdminDashboardPage;
